import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import { BoxProps, TypographyProps } from '@material-ui/core';
import Image, { StaticImageData } from 'next/image';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import affordableImg from '@images/food-program-page/advantages/affordable.png';
import highQualityImg from '@images/food-program-page/advantages/highQuality.png';
import personalImg from '@images/food-program-page/advantages/personal.png';
import quickImg from '@images/food-program-page/advantages/quick.png';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { FoodProgramH2 } from 'static_pages/foodProgram/Utils';
import Container from 'system/components/Container';

const ADVANTAGES = [
  {
    titleKey: 'advantages.personal.title',
    descriptionKey: 'advantages.personal.description',
    image: personalImg,
  },
  {
    titleKey: 'advantages.quick.title',
    descriptionKey: 'advantages.quick.description',
    image: quickImg,
  },
  {
    titleKey: 'advantages.highQuality.title',
    descriptionKey: 'advantages.highQuality.description',
    image: highQualityImg,
  },
  {
    titleKey: 'advantages.affordable.title',
    descriptionKey: 'advantages.affordable.description',
    image: affordableImg,
  },
];

const Advantages = (props: BoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation('food_program');
  const isSm = useBreakpointDown('xs');

  return (
    <Box as="section" textAlign="center" {...props}>
      <Container size="sm">
        <FoodProgramH2 mb={9}>
          <Trans
            t={t}
            i18nKey="advantages.title"
            components={{
              '1': <br />,
              '2': (
                <Box key="1" as="span" color={theme.palette.secondary[400]} />
              ),
            }}
          />
        </FoodProgramH2>
      </Container>
      <Container size={960}>
        <AdvantageList>
          {ADVANTAGES.map(({ titleKey, descriptionKey, image }, index) => {
            let imgOrder = 0;
            if (!(index % 2 === 0)) {
              imgOrder = 1;
            }
            if (isSm) {
              imgOrder = 0;
            }

            return (
              <Advantage key={titleKey}>
                <Box flexShrink="0" order={imgOrder}>
                  <AdvantageImage src={image} />
                </Box>
                <Box flexGrow={1}>
                  <Box flexDirection="column" display="flex" maxWidth={650}>
                    <AdvantageTitle>{t(titleKey)}</AdvantageTitle>
                    <AdvantageDescription>
                      <Trans
                        t={t}
                        i18nKey={descriptionKey}
                        components={[<strong key="1" />]}
                      />
                    </AdvantageDescription>
                  </Box>
                </Box>
              </Advantage>
            );
          })}
        </AdvantageList>
      </Container>
    </Box>
  );
};

const AdvantageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)}px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    gap: ${({ theme }) => theme.spacing(5)}px;
  }
`;

const AdvantageImage = (props: { src: string | StaticImageData }) => {
  const isSm = useBreakpointDown('sm');
  const baseWidth = 257;
  const baseHeight = 275;

  return (
    <Image
      src={props.src}
      width={isSm ? baseWidth * 0.8 : baseWidth}
      height={isSm ? baseHeight * 0.8 : baseHeight}
      objectFit="contain"
      alt=""
    />
  );
};

const Advantage = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: start;
  gap: ${({ theme }) => theme.spacing(3)}px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    gap: ${({ theme }) => theme.spacing(9)}px;
    flex-direction: row;
  }
`;

const AdvantageTitle = (props: TypographyProps) => (
  <Typography
    component="h3"
    fontSize={{ xs: 18, sm: 20, md: 36 }}
    mb={1}
    fontWeight={600}
    {...props}
  />
);

const AdvantageDescription = (props: TypographyProps) => (
  <Typography fontSize={{ xs: 14, sm: 16 }} {...props} />
);

export default Advantages;
